/**
 * What DANI is told about a visit pull in the chat (M4S-5904): the list Studio
 * saved from the person's file, and the pull's result. DANI never sees a
 * panelist ID or a coordinate: it names the list in a `feasibility-visits`
 * block, the card runs the pull, and the card holds the file. Pure.
 */
import {
    readsPings,
    type SavedList,
    type VisitPullResponse,
    type VisitsBlock,
} from '../contracts/pql';
import { LAUNCH_HEADER, STUDIO_RESULT_HEADER } from './primer-marks';

const fmt = (n: number) => n.toLocaleString('en-US');

/** Places once each: a list often repeats a row per creative or week. */
export function uniquePlaceCount(list: Pick<SavedList, 'placements' | 'matched'>): number {
    if (!list.placements?.length) return list.matched;
    return new Set(list.placements.map((p) => `${p.lat.toFixed(6)},${p.lng.toFixed(6)}`)).size;
}

/** The list Studio saved from the person's file, and how to pull from it. */
export function visitsListForDani(list: SavedList, ask: string, rules: string | null): string {
    const places = uniquePlaceCount(list);
    const radii = (list.placements ?? []).map((p) => p.radiusMeters);
    const radius = radii.length
        ? Math.min(...radii) === Math.max(...radii)
            ? `${radii[0]} m around each`
            : `${Math.min(...radii)}–${Math.max(...radii)} m around each`
        : null;
    return [
        LAUNCH_HEADER,
        `The person attached ${list.name} and asked: "${ask}". Studio saved it as a placement list: listId "${list.id}", ${fmt(places)} places${radius ? `, ${radius} by default` : ''}.`,
        ...(list.unmatched
            ? [
                  `${fmt(list.unmatched)} row${list.unmatched === 1 ? '' : 's'} of the file could not be placed (an address not found, or no coordinates) and ${list.unmatched === 1 ? 'is' : 'are'} left out: say so in one sentence.`,
              ]
            : []),
        '',
        'This is a request for the people at those places, not a survey estimate. In this same reply, say in one sentence what you will pull and write one feasibility-visits block: {"listId": "<the id above>", "presence": "passed", "from": "YYYY-MM-DD", "to": "YYYY-MM-DD"}, adding "radiusMeters" only when the person named a distance. Use "presence": "visit" only when the person wants people who stopped and stayed, not everyone who passed. If the person gave no dates, ask for them and write no block. To the person it is their file: never call it a list. Studio runs the pull, shows the count and offers the file of panelist IDs; never list IDs yourself.',
        ...(rules ? ['', rules] : []),
    ].join('\n');
}

/** The pull's result, for DANI to explain in a sentence or two. */
export function visitsBriefForDani(
    block: VisitsBlock,
    listName: string,
    result: VisitPullResponse,
): string {
    const who =
        result.presence === 'either'
            ? `anyone who passed or stayed at ${listName}${block.radiusMeters ? ` (${block.radiusMeters} m radius)` : ''}, drive-bys included`
            : readsPings(result.presence)
              ? `anyone whose phone passed ${listName}${block.radiusMeters ? ` (${block.radiusMeters} m radius)` : ''}, drive-bys included`
              : `people who stopped and stayed at ${listName}`;
    return [
        STUDIO_RESULT_HEADER,
        `Visit pull: ${who}, ${block.from} to ${block.to}.`,
        `${fmt(result.count)} active panelists you can survey${readsPings(result.presence) && result.stopped != null ? `; ${fmt(result.stopped)} of them stopped (two or more pings at one place on one day)` : ''}.`,
        ...(readsPings(result.presence) && result.placesWithNoOne
            ? [
                  `${fmt(result.placesWithNoOne)} place${result.placesWithNoOne === 1 ? '' : 's'} in the file caught no one: mention it, and suggest checking those coordinates in the by-place file.`,
              ]
            : []),
        'The card offers one Excel workbook (Summary, People, By place, By day), the panelist IDs and the by-place list as CSV, and opens the pull in Visit history for a release group.',
        'Tell the person the number in one sentence and point them to the card for the file. Never list or retype panelist IDs. If they want it wider or narrower, offer the other choice (anyone who passed by, or people who stopped and stayed), a different distance or different dates.',
    ].join('\n');
}
